import {
  ISSUE_FINISHED, ISSUES_FINISHED, ISSUE_REOPENED,
  ISSUES_LIST_REFRESH, ISSUES_FORGET_FINISHED,
  ROW_SETTLED, ROWS_SETTLED, ROW_UNSETTLED, ROWS_SETTLE_SWEEP,
} from "../constants/action-types/issues";

// How long a decided row keeps its badge before it leaves the list.
export const SETTLE_MS = 1100;

// Which issues this session has finished, and a tick every list refetches on.
//
// `finished` is an id -> terminal status map ("resolved" / "abandoned" /
// "rejected"). It is a HINT, not a truth: the server decides, and this only
// exists so the view redraws without the row it just ended. `reopened` is the
// other direction - an id whose row may still SAY resolved while the user has
// put it back into play, so a status-based hide must not swallow it.
const initialState = {
  finished: {},
  reopened: {},
  refreshTick: 0,
  // scope -> id -> { label, at }. What a list shows on a row the server
  // has just confirmed as decided, for one beat, before dropping it.
  settled: {},
};

export default function issuesReducer(state = initialState, action) {
  switch (action.type) {
    case ISSUE_FINISHED: {
      const { id, status } = action.payload || {};
      if (!id) return state;
      const reopened = { ...state.reopened };
      delete reopened[id];
      return { ...state, finished: { ...state.finished, [id]: status || "resolved" }, reopened };
    }

    case ISSUES_FINISHED: {
      const { ids, status } = action.payload || {};
      if (!ids || !ids.length) return state;
      const finished = { ...state.finished };
      const reopened = { ...state.reopened };
      for (const id of ids) { finished[id] = status || "resolved"; delete reopened[id]; }
      return { ...state, finished, reopened };
    }

    case ISSUE_REOPENED: {
      const { id } = action.payload || {};
      if (!id) return state;
      const finished = { ...state.finished };
      delete finished[id];
      return { ...state, finished, reopened: { ...state.reopened, [id]: true } };
    }

    case ISSUES_LIST_REFRESH:
      return { ...state, refreshTick: state.refreshTick + 1 };

    case ISSUES_FORGET_FINISHED:
      return { ...state, finished: {}, reopened: {} };

    case ROW_SETTLED: {
      const { scope, id, label, at } = action.payload || {};
      if (!scope || !id) return state;
      const bucket = { ...(state.settled[scope] || {}), [id]: { label, at } };
      return { ...state, settled: { ...state.settled, [scope]: bucket } };
    }

    case ROWS_SETTLED: {
      const { scope, ids, label, at } = action.payload || {};
      if (!scope || !ids || !ids.length) return state;
      const bucket = { ...(state.settled[scope] || {}) };
      for (const id of ids) bucket[id] = { label, at };
      return { ...state, settled: { ...state.settled, [scope]: bucket } };
    }

    case ROW_UNSETTLED: {
      const { scope, id } = action.payload || {};
      if (!scope || !id || !state.settled[scope] || !(id in state.settled[scope])) return state;
      const bucket = { ...state.settled[scope] };
      delete bucket[id];
      return { ...state, settled: { ...state.settled, [scope]: bucket } };
    }

    case ROWS_SETTLE_SWEEP: {
      // Drop every record whose beat ended before `before`. Returns the
      // SAME state when nothing changes, so subscribers do not re-render.
      const before = (action.payload && action.payload.before) || 0;
      let changed = false;
      const next = {};
      for (const scope of Object.keys(state.settled)) {
        const bucket = {};
        for (const [id, rec] of Object.entries(state.settled[scope])) {
          if (rec.at + SETTLE_MS <= before) { changed = true; continue; }
          bucket[id] = rec;
        }
        next[scope] = bucket;
      }
      return changed ? { ...state, settled: next } : state;
    }

    default:
      return state;
  }
}
